import type { LocaleIso } from "@solvari/translations";

import { getAbTestFeature } from "@solvari/ab-testing";
import { createGtmEvent } from "@solvari/argus";
import { getJumpLinkMenus } from "@solvari/components/blade/s-jumplinks/sJumplinks.ts";

type FormProps = {
  ctaText: string;
  formGroupId: number | null;
  formId: number | null;
  formUrl: {
    isDefaultUrl: boolean;
    url: string;
  };
  formVariantName?: string;
  locale: LocaleIso;
  revisionId: number | null;
};

// First test scrolls to form itself. Later on we want to scroll to container and compare the results.
// Uncomment everything in this file when proceeding to the second test.
// const formContainerId = "dynamic-form-intersection";

const formWidgetId = "form-widget";

function parseFormProps(propsString: string): FormProps | null {
  try {
    return JSON.parse(propsString) as FormProps;
  } catch {
    return null;
  }
}

const formWidget = document.querySelector<HTMLElement>(`#${formWidgetId}`);
// const formContainer = document.querySelector<HTMLElement>(
//   `#${formContainerId}`,
// );

function getFormProps() {
  if (!formWidget) {
    return null;
  }

  const formWidgetProps = formWidget.dataset.props;
  if (!formWidgetProps) {
    return null;
  }

  return parseFormProps(formWidgetProps);
}

function getFormLinkUrl() {
  if (!formWidget) {
    return null;
  }

  const isAbTestEnabled = getAbTestFeature<boolean>(
    "form-jumplinks",
    true,
  ).value;

  if (isAbTestEnabled) {
    // Also uncomment this after first test is done
    // return formContainer ? `#${formContainerId}` : `#${formWidgetId}`;
    return `#${formWidgetId}`;
  }

  const formProps = getFormProps();

  if (formProps?.formUrl.isDefaultUrl) {
    void createGtmEvent("form-links-use-default-url");
  }
  return formProps?.formUrl.url ?? null;
}

function addFormLinkToJumplinkMenus(formLink: string, ctaText: string) {
  getJumpLinkMenus().forEach((menuEl: HTMLElement) => {
    const menuListEl = menuEl.querySelector<HTMLElement>(".s-jumplinks");
    if (!menuListEl) {
      return;
    }
    const formLinkListEl = document.createElement("li");
    formLinkListEl.classList.add(
      "s-jumplinks__jumplink",
      "s-jumplinks__jumplink--default",
    );
    const formLinkAnchorEl = document.createElement("a");
    formLinkAnchorEl.classList.add("s-jumplinks__jumplink__link");
    formLinkAnchorEl.href = formLink;
    formLinkAnchorEl.textContent = ctaText;
    formLinkListEl.append(formLinkAnchorEl);
    menuListEl.append(formLinkListEl);
  });
}

function initFormJumplinks(): void {
  const formLink = getFormLinkUrl();
  if (!formLink) {
    return;
  }

  const formJumplinks = document.querySelectorAll<
    HTMLAnchorElement | HTMLFormElement
  >("[data-form-link]");
  const sidebarCta = document.querySelector<HTMLAnchorElement>(
    ".sidebar-nav-container__cta",
  );

  formJumplinks.forEach((formLinkEl) => {
    if (formLinkEl instanceof HTMLAnchorElement) {
      formLinkEl.href = formLink;
    } else if (formLinkEl instanceof HTMLFormElement) {
      formLinkEl.action = formLink;
    }
  });

  if (sidebarCta) {
    const formProps = getFormProps();
    if (formProps?.ctaText) {
      sidebarCta.innerText = formProps.ctaText;
    }
    addFormLinkToJumplinkMenus(formLink, sidebarCta.innerText);
  }
}

export { getFormLinkUrl, initFormJumplinks };
